import { NativeBridgeResponse } from 'scbk-services';
import { useNativeBridge } from './useNativeBridge';

export namespace Pdf {
    export type PdfInfo = {
        title: string;
        pdfUrl: string;
    };

    export type PdfResult = {
        title: string;
        pdfUrl: string;
        confirm: boolean;
        download: boolean;
        scrollDone?: 'Y' | 'N' | '';
    };

    export type PdfDataInfo = {
        pdfData: string;
        bancaDownloadCheck: 'true' | 'false';
    } & Pick<PdfInfo, 'title'>;

    export interface DoNPdfViewerRequest {
        url: PdfInfo[] | undefined;
    }
    export interface DoNPdfViewerResponse {
        result: PdfResult[] | string;
    }

    export interface DoNPdfDataViewerRequest {
        url: PdfDataInfo[] | undefined;
    }

    export interface DoNPdfDataViewerResponse {
        confirm: boolean;
    }
}

/**
 * PDF 뷰어 네이티브 브릿지 hook
 * @returns
 */
const usePdfViewerBridge = () => {
    const nativeBridge = useNativeBridge();

    /**
     * PDF 파일다운로드 요청
     * @param url 호출 url
     * @param fileName 파일명
     */
    const doNPDFDownload = async (url: string, fileName: string) => {
        await nativeBridge(
            'doNPDFDownload',
            { url, fileName: encodeURIComponent(fileName) },
            { onlyRequest: true },
        );
    };

    /**
     * PDF view 요청
     * @param request
     * @returns
     */
    const doNPdfViewer = async (
        request: Pdf.DoNPdfViewerRequest,
        callback?: (response: NativeBridgeResponse<Pdf.DoNPdfViewerResponse>) => void,
    ) => {
        const response = await nativeBridge<Pdf.DoNPdfViewerRequest, Pdf.DoNPdfViewerResponse>(
            'doNPdfViewer',
            request,
            {
                timeoutMillis: -1,
            },
        );

        if (callback) {
            callback(response);
        } else {
            const { status, payload } = response;

            if (status === 'fail') {
                return;
            }

            if (!payload) {
                return;
            }

            return payload.result;
        }
    };

    /**
     * PDF view요청(PDF Scroll 하단 touch 여부(끝까지읽음) 추가버전)
     * @param request
     * @returns
     */
    const doNPdfViewAndScroll = async (
        request: Pdf.DoNPdfViewerRequest,
        callback?: (response: NativeBridgeResponse<Pdf.DoNPdfViewerResponse>) => void,
    ) => {
        const response = await nativeBridge<Pdf.DoNPdfViewerRequest, Pdf.DoNPdfViewerResponse>(
            'doNPdfViewAndScroll',
            request,
            {
                timeoutMillis: -1,
            },
        );

        if (callback) {
            callback(response);
        } else {
            const { status, payload } = response;

            if (status === 'fail') {
                return;
            }

            if (!payload) {
                return;
            }

            return payload.result;
        }
    };

    /**
     * PDF view요청(Base64 PDF 데이터로 확인)
     * @param request
     * @returns
     */
    const doNPdfDataViewer = async (request: Pdf.DoNPdfDataViewerRequest) => {
        const res = await nativeBridge<Pdf.DoNPdfDataViewerRequest, Pdf.DoNPdfDataViewerResponse[]>(
            'doNPdfDataViewer',
            request,
        );
        if (res.status === 'fail') {
            return;
        }

        return res.payload;
    };

    return {
        doNPDFDownload,
        doNPdfViewer,
        doNPdfViewAndScroll,
        doNPdfDataViewer,
    };
};

export { usePdfViewerBridge };
